import DeleteIcon from '@mui/icons-material/Delete';
import {
  Button,
  FormHelperText,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Box } from '@mui/system';
import { useFormik, type FormikProps } from 'formik';
import { useEffect } from 'react';
import * as yup from 'yup';
import { useAgrupacionArticulosStore } from '../../stores/agrupaciones-articulos/agrupaciones-articulos.store';
import { useGrupoArticulosStore } from '../../stores/grupos-articulos/grupos-articulos.store';
import { useUIStore } from '../../stores/ui/ui.store';
import transformToOptions from '../../utils/transform-to-options';
import ComboBox from '../ui/Combobox';
import TabPanel from '../ui/TabPanel';
import type { ArticuloFormValues } from './articulo.schema';

type GrupoAgrupacionTabProps = {
  value: number;
  formik: FormikProps<ArticuloFormValues>;
  inactivo: boolean;
  loading: boolean;
};

const GrupoAgrupacionTab = ({
  value,
  formik: articuloForm,
  inactivo,
  loading
}: GrupoAgrupacionTabProps) => {
  const { showWarningToast } = useUIStore((state) => state);
  const { actives: grupos, cargarActivos: cargarGruposActivos } = useGrupoArticulosStore(
    (state) => state
  );
  const { actives: agrupaciones, cargarPorIdGrupo } = useAgrupacionArticulosStore((state) => state);
  const agrupacionForm = useFormik({
    initialValues: { idAgrupacion: 0, idGrupo: 0 },
    validationSchema: yup.object({
      idAgrupacion: yup.number().moreThan(0, 'Debe seleccionar una agrupación'),
      idGrupo: yup.number().moreThan(0, 'Debe seleccionar un grupo')
    }),
    onSubmit: (values) => {
      const grupo = grupos.find((x: any) => x.id === +values.idGrupo);
      const agrupacion = agrupaciones.find((x: any) => x.id === +values.idAgrupacion);
      const existe = articuloForm.values.agrupaciones.some((a: any) => a.idGrupo === grupo?.id);
      if (existe) {
        showWarningToast('El grupo ya fue agregado a la lista');
        return;
      }

      articuloForm.setFieldValue('agrupaciones', [
        ...articuloForm.values.agrupaciones,
        { id: agrupacion?.id, nombre: agrupacion?.nombre, idGrupo: grupo?.id, grupo: grupo?.nombre }
      ]);
      agrupacionForm.resetForm();
    },
    enableReinitialize: true
  });

  useEffect(() => {
    cargarGruposActivos();
  }, []);

  useEffect(() => {
    if (!agrupacionForm.values.idGrupo) return;
    cargarPorIdGrupo(agrupacionForm.values.idGrupo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agrupacionForm.values.idGrupo]);

  const quitarAgrupacion = (id: number) => {
    articuloForm.setFieldValue(
      'agrupaciones',
      articuloForm.values.agrupaciones.filter((x) => x.id !== id)
    );
  };

  const gruposOptions = transformToOptions(grupos);
  const agrupacionesOptions = transformToOptions(agrupaciones);

  return (
    <TabPanel value={value} index={0}>
      <Grid container justifyContent="space-around">
        <Grid size={3}>
          <Grid container direction="column" spacing={2}>
            <Grid>
              <ComboBox
                label="Grupo"
                name="idGrupo"
                disabled={loading || inactivo}
                value={agrupacionForm.values.idGrupo}
                onChange={agrupacionForm.handleChange}
                error={agrupacionForm.touched.idGrupo && Boolean(agrupacionForm.errors.idGrupo)}
                helperText={agrupacionForm.touched.idGrupo ? agrupacionForm.errors.idGrupo : ''}
                options={gruposOptions}
              />
            </Grid>
            <Grid>
              <ComboBox
                label="Agrupación"
                name="idAgrupacion"
                disabled={loading || inactivo}
                value={agrupacionForm.values.idAgrupacion}
                onChange={agrupacionForm.handleChange}
                error={
                  agrupacionForm.touched.idAgrupacion && Boolean(agrupacionForm.errors.idAgrupacion)
                }
                helperText={
                  agrupacionForm.touched.idAgrupacion ? agrupacionForm.errors.idAgrupacion : ''
                }
                options={agrupacionesOptions}
              />
            </Grid>
            <Grid>
              <Button
                variant="contained"
                disabled={loading || inactivo}
                onClick={() => agrupacionForm.handleSubmit()}
                disableElevation
                fullWidth
              >
                Agregar
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={6}>
          <Stack direction="column" component={Paper}>
            <TableContainer>
              <Table sx={{ width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell width={70} />
                    <TableCell>Grupo</TableCell>
                    <TableCell>Agrupación</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {articuloForm.values.agrupaciones.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell width={70}>
                        <IconButton
                          color="error"
                          onClick={() => quitarAgrupacion(a.id)}
                          disabled={inactivo || loading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell>{a.grupo}</TableCell>
                      <TableCell>{a.nombre}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {articuloForm.touched.agrupaciones && Boolean(articuloForm.errors.agrupaciones) && (
              <Box p={2}>
                <FormHelperText error={true}>
                  {articuloForm.errors.agrupaciones?.toString()}
                </FormHelperText>
              </Box>
            )}
          </Stack>
        </Grid>
      </Grid>
    </TabPanel>
  );
};

export default GrupoAgrupacionTab;
